import { useCallback, useEffect, useMemo, useState } from 'react';

import isEqual from 'react-fast-compare';
import { useNavigate } from 'react-router-dom';

import { Mutable } from '@rbi-ctg/frontend';
import { IOffer } from '@rbi-ctg/menu';
import { useListOffersByUserIdQuery } from 'generated/rbi-graphql';
import { IOffersFragment } from 'generated/sanity-graphql';
import { useOffersDataset } from 'hooks/use-offers-dataset';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { IDayPartBoundary } from 'state/day-part/hooks/use-active-day-parts';
import { useActivateOfferMutation } from 'state/graphql/hooks/use-activate-offer-mutation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IMParticleCtx } from 'state/mParticle';
import { Query } from 'state/network/hook';
import { ServiceMode } from 'state/service-mode';
import { ILogger } from 'utils/logger';
import noop from 'utils/noop';

import {
  combineSortedOffersWithAvailableOfferCounts,
  countAvailableOffersByCouponId
} from '../util';

import { shouldSkipOffersByUserIdQueryForCurrentBrand } from './constants';
import useCartPromoCodeOffers from './use-cart-promo-code-offers';
import useLockedOffers from './use-locked-offers';
import useOffersList from './use-offer-list';
import useOfferRedemption from './use-offer-redemption';
import useOfferUnavailableModal from './use-offer-unavailable-modal';
import usePromoCodeOffers from './use-promo-code-offers';
import useSelectedOffer from './use-selected-offer';

interface IUseOffersProps {
  activeDayParts: IDayPartBoundary[];
  decorateLogger<O extends object>(object: O): void;
  logger: ILogger;
  mParticle: IMParticleCtx;
  query: Query;
  serviceMode: ServiceMode | null;
  user: UserDetails | null;
}

export default function useOffers({
  decorateLogger,
  logger,
  mParticle,
  query,
  serviceMode,
  user
}: IUseOffersProps) {
  const enableQueryOffersFromBackend = useFlag(LaunchDarklyFlag.ENABLE_QUERY_OFFERS_FROM_BACKEND);
  const [sortedOffers, setSortedOffers] = useState<IOffer[]>([]);
  const [filteredOffers, setFilteredOffers] = useState<IOffer[]>([]);
  const [offersToDisplay, setOffersToDisplay] = useState<IOffer[]>([]);
  const [activateOfferMutation] = useActivateOfferMutation();
  const [numberAvailableByCouponId, setNumberAvailableByCouponId] = useState<{
    [key: string]: number;
  }>({});
  const navigate = useNavigate();

  const {
    data: offersData,
    loading: sortedOffersLoading,
    refetch: refetchSortedOffers
  } = useOffersDataset();

  const {
    data: offersByUserId,
    refetch: refetchOffersByUserIdOriginal,
    loading: offersByUserIdLoading
  } = useListOffersByUserIdQuery({
    fetchPolicy: 'cache-and-network',
    skip: !user || shouldSkipOffersByUserIdQueryForCurrentBrand
  });

  const refetchOffersByUserId = user ? refetchOffersByUserIdOriginal : noop;

  useEffect(() => {
    if (!offersByUserId) {
      return;
    }

    const counts = offersByUserId && countAvailableOffersByCouponId(offersByUserId);
    setNumberAvailableByCouponId(counts || {});
  }, [offersByUserId]);

  /**
   * combine sortedOffers with numberAvailableByCouponId for all offer lists to consume
   */
  useEffect(() => {
    if (!enableQueryOffersFromBackend) {
      if (!offersData?.sortedOffers || !numberAvailableByCouponId) {
        return;
      }

      const sortedOffersWithCounts = combineSortedOffersWithAvailableOfferCounts(
        offersData.sortedOffers as IOffersFragment[],
        numberAvailableByCouponId
      );

      setSortedOffers((sortedOffersWithCounts as Mutable<IOffersFragment[]>) as IOffer[]);
    }
  }, [enableQueryOffersFromBackend, offersData, numberAvailableByCouponId]);

  const {
    evaluateUserOffersLoading,
    offers,
    offersFeedback,
    queryRedeemableOffers,
    redeemUnauthenticatedOffer,
    refetch: refetchOffersList,
    standardOffers
  } = useOffersList({
    serviceMode,
    sortedOffers,
    user
  });

  /**
   * combine sortedOffers with numberAvailableByCouponId for all offer lists to consume
   */
  useEffect(() => {
    if (enableQueryOffersFromBackend) {
      if (!offers || !numberAvailableByCouponId) {
        return;
      }
      const sortedOffersWithCounts = combineSortedOffersWithAvailableOfferCounts(
        offers as IOffersFragment[],
        numberAvailableByCouponId
      );
      setFilteredOffers((sortedOffersWithCounts as Mutable<IOffersFragment[]>) as IOffer[]);
    }
  }, [enableQueryOffersFromBackend, offers, numberAvailableByCouponId]);

  const refreshOffers = useCallback(
    /**
     * Refetch sorted offers, offersByUserId, and evaluateUserOffers in parallel
     */
    async () => {
      try {
        if (enableQueryOffersFromBackend) {
          await Promise.all([
            refetchOffersList(),
            refetchOffersByUserId() as any,
            queryRedeemableOffers()
          ]);
        } else {
          await Promise.all([
            refetchSortedOffers(),
            refetchOffersByUserId() as any,
            queryRedeemableOffers()
          ]);
        }
      } catch (error) {
        // TODO: retry or error state
        logger.error({ error, message: 'Refresh Offers Error' });
      }
      return;
    },
    [
      enableQueryOffersFromBackend,
      logger,
      queryRedeemableOffers,
      refetchOffersByUserId,
      refetchOffersList,
      refetchSortedOffers
    ]
  );

  const activateOffer = useCallback(
    /**
     * Activates an offer
     *
     * @param tokenId pulled from offerFeedback
     */
    async (tokenId: string) => {
      try {
        const resp = await activateOfferMutation({ variables: { tokenId } });
        const isOfferActivated = resp?.data?.activateLoyaltyOffer || false;
        return { isOfferActivated };
      } catch (error) {
        logger.error({ error, message: 'Activate Offer Error' });
        throw error;
      } finally {
        refreshOffers();
      }
    },
    [activateOfferMutation, refreshOffers, logger]
  );

  const sortedOffersToUse = useMemo(() => {
    return enableQueryOffersFromBackend ? filteredOffers : sortedOffers;
  }, [enableQueryOffersFromBackend, filteredOffers, sortedOffers]);

  const { lockedOffers } = useLockedOffers({
    sortedOffers: sortedOffersToUse,
    offersFeedback
  });

  /**
   * promo code offers are separated out because we want to be able to
   *  update their redeemable state without refetching all the offers
   */
  const { promoCodeOffers, togglePromoCodeOfferRedeemable } = usePromoCodeOffers({
    sortedOffers: sortedOffersToUse,
    offersFeedback
  });

  /**
   * offersToDisplay combines the base sorted offers (either from FE or BE)
   *  as well as any promo code offers. This effect listens for any changes
   *  to either the main offers or promo code offers and updates the offersToDisplay
   * example of change: a user unlocks a promo code offer. instead of re-fetching all
   *  offers to get the updated unlocked state, we just update the state on the FE
   *  and update this setOffersToDisplay state.
   */
  useEffect(() => {
    const baseOffers = enableQueryOffersFromBackend ? standardOffers : offers;
    const newOffersToDisplay = [...promoCodeOffers, ...baseOffers];
    setOffersToDisplay(previousOffers =>
      isEqual(previousOffers, newOffersToDisplay) ? previousOffers : newOffersToDisplay
    );
  }, [enableQueryOffersFromBackend, offers, promoCodeOffers, setOffersToDisplay, standardOffers]);

  /**
   * cart promo code offers are separated out because they are not
   *  displayed on the offers page and have a completely separate UX
   */
  const { cartPromoCodeOffers } = useCartPromoCodeOffers({
    sortedOffers: sortedOffersToUse,
    offersFeedback
  });

  const {
    clearSelectedOffer,
    isSelectedOfferCartEntry,
    selectOfferById,
    selectedOffer,
    selectedOfferCartEntry,
    selectedOfferPrice,
    setSelectedOfferCartEntry,
    setSelectedOfferPrice
  } = useSelectedOffer({
    decorateLogger,
    mParticle,
    offers: offersToDisplay,
    lockedOffers,
    serviceMode,
    cartPromoCodeOffers
  });

  const {
    offerValidationErrors,
    onCancelRedemption,
    onConfirmRedemption,
    onSelectMobileRedemption,
    onSelectRestaurantRedemption,
    redemptionMethod,
    setOfferValidationErrors,
    setRedemptionMethod,
    validateOfferRedeemable
  } = useOfferRedemption({
    clearSelectedOffer,
    decorateLogger,
    logger,
    mParticle,
    query,
    queryRedeemableOffers,
    redeemUnauthenticatedOffer,
    user
  });

  const [OfferUnavailableDialog, openOfferUnavailableDialog] = useOfferUnavailableModal({
    navigate,
    onCancelRedemption
  });

  return {
    OfferUnavailableDialog,
    clearSelectedOffer,
    isSelectedOfferCartEntry,
    loading: evaluateUserOffersLoading || sortedOffersLoading || offersByUserIdLoading,
    offers: offersToDisplay,
    offersFeedback,
    offerValidationErrors,
    lockedOffers,
    promoCodeOffers,
    togglePromoCodeOfferRedeemable,
    cartPromoCodeOffers,
    onCancelRedemption,
    onConfirmRedemption,
    onSelectMobileRedemption,
    onSelectRestaurantRedemption,
    openOfferUnavailableDialog,
    redemptionMethod,
    selectOfferById,
    selectedOffer,
    selectedOfferCartEntry,
    selectedOfferPrice,
    setOfferValidationErrors,
    setRedemptionMethod,
    setSelectedOfferCartEntry,
    setSelectedOfferPrice,
    queryRedeemableOffers,
    validateOfferRedeemable,
    refreshOffers,
    activateOffer
  };
}
